import React from "react";
import {TFunction} from "i18next";
import {ColumnDef, FilterDef, FilterOperator, FilterType} from "../../../components/ExTable";
import {DeviceHistoryEntry, DeviceHistoryEntryField, DeviceHistorySeverity} from "../../../app/api/model/deviceHistory";
import {DateFormat, formatTimestamp} from "../../../utils/datetime";
import {getTranslation, getTranslationKey, TranslationContext} from "../../../utils/getTranslationKey";
import {Error as ErrorIcon, Info as InfoIcon, Warning as WarningIcon} from "@mui/icons-material";
import {formatInformationCellContent} from "./components/formatInformationCellContent";
import {Stage} from "../../../app/api/model/stage";
import {EetsDeviceHistoryEntry} from "../../../app/api/model/eetsDevice";

export function getDeviceHistoryColumnDefs<T extends DeviceHistoryEntry | EetsDeviceHistoryEntry>(
    t: TFunction,
    setViewJson: React.Dispatch<React.SetStateAction<object | undefined>>,
    setViewText: React.Dispatch<React.SetStateAction<string | undefined>>,
    isEetsDevice = false
): ColumnDef<T>[] {
    return [
        {
            fieldName: DeviceHistoryEntryField.TIMESTAMP,
            align: "left",
            label: t("deviceHistory_timestamp"),
            width: 65,
            sortable: false,
            formatCellText: ({cellValue}: {cellValue: string}) => {
                return formatTimestamp(cellValue, DateFormat.DATE_HOURS_MINUTES_SECONDS);
            },
        },
        {
            fieldName: DeviceHistoryEntryField.TYPE,
            align: "left",
            label: t("deviceHistory_type"),
            width: 100,
            sortable: false,
            formatCellText: ({cellValue}: {cellValue: string}) => {
                return getTranslation({
                    context: isEetsDevice
                        ? TranslationContext.EETS_DEVICE_HISTORY_TYPE
                        : TranslationContext.DEVICE_HISTORY_TYPE,
                    value: cellValue,
                    t: t,
                });
            },
        },
        {
            fieldName: DeviceHistoryEntryField.INFORMATION,
            align: "left",
            label: t("deviceHistory_information"),
            width: 400,
            sortable: false,
            formatCellText: formatInformationCellContent(setViewJson, setViewText),
        },
        {
            fieldName: DeviceHistoryEntryField.SEVERITY,
            label: t("deviceHistory_severity"),
            width: 60,
            sortable: false,
            formatCellText: ({row}: {row: any}) => {
                switch (row.severity) {
                    case DeviceHistorySeverity.WARN:
                        return {
                            title: t(
                                getTranslationKey({
                                    context: TranslationContext.DEVICE_HISTORY_SEVERITY,
                                    value: "warn",
                                })
                            ),
                            node: <WarningIcon color="warning" />,
                        };
                    case DeviceHistorySeverity.ERROR:
                        return {
                            title: t(
                                getTranslationKey({
                                    context: TranslationContext.DEVICE_HISTORY_SEVERITY,
                                    value: "error",
                                })
                            ),
                            node: <ErrorIcon color="error" />,
                        };
                    default:
                        return {
                            title: t(
                                getTranslationKey({
                                    context: TranslationContext.DEVICE_HISTORY_SEVERITY,
                                    value: "info",
                                })
                            ),
                            node: <InfoIcon color="info" />,
                        };
                }
            },
        },
        {
            fieldName: DeviceHistoryEntryField.FIRMWARE_VERSION,
            label: t("firmware_version"),
            width: 40,
            sortable: false,
        },
        ...(!isEetsDevice
            ? ([
                  {
                      fieldName: DeviceHistoryEntryField.STAGE_IDENTIFIER,
                      label: t("deviceHistory_stage"),
                      width: 100,
                      sortable: false,
                  },
                  {
                      fieldName: DeviceHistoryEntryField.ORGANIZATION_NAME,
                      label: t("deviceHistory_organization"),
                      width: 100,
                      sortable: false,
                  },
                  {
                      fieldName: DeviceHistoryEntryField.USER_COMMENT,
                      label: t("deviceHistory_comment"),
                      width: 100,
                      sortable: false,
                  },
              ] as ColumnDef<T>[])
            : []),
        {
            fieldName: DeviceHistoryEntryField.USER_TAG,
            label: t("deviceHistory_user"),
            width: 75,
            sortable: false,
        },
    ];
}

export function getDeviceHistoryFilterDefs<T extends DeviceHistoryEntry | EetsDeviceHistoryEntry>(
    t: TFunction,
    isEetsDevice = false,
    stageList?: Stage[]
): FilterDef<T>[] {
    return [
        {
            fieldName: DeviceHistoryEntryField.TYPE,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: [
                {value: "checkin", labelKey: "deviceHistoryType_checkin"},
                {value: "checkout", labelKey: "deviceHistoryType_checkout"},
                {value: "lastWill", labelKey: "deviceHistoryType_lastWill"},
                {value: "firmwareUpdate", labelKey: "deviceHistoryType_firmwareUpdate"},
                {value: "rdl", labelKey: "deviceHistoryType_rdl"},
                {value: "rdlAuth", labelKey: "deviceHistoryType_rdlAuth"},
                {value: "rdlMassStorage", labelKey: "deviceHistoryType_rdlMassStorage"},
                {value: "rdlDriverCard", labelKey: "deviceHistoryType_rdlDriverCard"},
                {value: "deviceAssignment", labelKey: "deviceHistoryType_deviceAssignment"},
                {value: "lastSystemState", labelKey: "deviceHistoryType_lastSystemState"},
                {value: "dtcoInfo", labelKey: "deviceHistoryType_dtcoInfo"},
                {value: "logfile", labelKey: "deviceHistoryType_logfile"},
                {value: "command", labelKey: "deviceHistoryType_command"},
            ],
        },
        {
            fieldName: DeviceHistoryEntryField.TIMESTAMP,
            filterOperators: [FilterOperator.LT, FilterOperator.GT, FilterOperator.LTE, FilterOperator.GTE],
            type: FilterType.DATE,
        },
        {
            fieldName: DeviceHistoryEntryField.SEVERITY,
            filterOperators: [FilterOperator.IN],
            type: FilterType.MULTI_CHOICE_ENUM,
            enumValues: [
                {value: 0, labelKey: "deviceHistorySeverity_info"},
                {value: 1, labelKey: "deviceHistorySeverity_warn"},
                {value: 2, labelKey: "deviceHistorySeverity_error"},
            ],
        },
        {
            fieldName: DeviceHistoryEntryField.JOB_ID,
            filterOperators: [FilterOperator.EQ],
            type: FilterType.NUMBER,
            label: t("deviceHistoryjobID"),
        },
        ...(!isEetsDevice
            ? ([
                  {
                      fieldName: DeviceHistoryEntryField.STAGE_IDENTIFIER,
                      filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
                      type: FilterType.ENUM,
                      enumValues:
                          stageList?.map((s) => {
                              return {value: s.identifier, labelKey: s.description};
                          }) ?? [],
                  },
                  {
                      fieldName: DeviceHistoryEntryField.ORGANIZATION_NAME,
                      filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
                  },
              ] as FilterDef<T>[])
            : []),
        {
            fieldName: DeviceHistoryEntryField.INFORMATION,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        ...(isEetsDevice
            ? ([
                  {
                      fieldName: DeviceHistoryEntryField.USER_COMMENT,
                      filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
                  },
              ] as FilterDef<T>[])
            : []),
        {
            fieldName: DeviceHistoryEntryField.USER_TAG,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
    ];
}

export function hasJsonStructure(json: any) {
    if (typeof json !== "string") return false;
    try {
        const result = JSON.parse(json);
        const type = Object.prototype.toString.call(result);
        return type === "[object Object]" || type === "[object Array]";
    } catch {
        return false;
    }
}
